import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Grid, TextField } from '@mui/material';

import { CustomerStatus } from '@/types';
import { useUpdateCustomerStatusMutation } from '@/services';
import { messages } from '@/utils/helpers';

export interface UpdateCustomerStatusProps extends DialogProps {
  customerStatus: CustomerStatus;
  onClose(): void;
}

const UpdateCustomerStatus: React.FC<UpdateCustomerStatusProps> = ({ customerStatus, onClose, ...props }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [updateCustomerStatus, updateCustomerStatusResponse] = useUpdateCustomerStatusMutation();

  const { values, errors, touched, handleSubmit, handleChange } = useFormik<CustomerStatus>({
    initialValues: customerStatus ?? {
      name: '',
      independent_from_depth: false,
      independent_from_interval: false,
      is_name_editable: false,
      is_simple: false,
    },
    enableReinitialize: true,
    validationSchema: yup.object().shape({
      name: yup.string().required(messages.form.REQUIRED),
      independent_from_depth: yup.bool(),
      independent_from_interval: yup.bool(),
      is_name_editable: yup.bool(),
      is_simple: yup.bool(),
    }),
    async onSubmit(customerStatus, { resetForm }) {
      try {
        await updateCustomerStatus(customerStatus).unwrap();

        onClose();
        resetForm();
      } catch (error) {
        enqueueSnackbar(error.data, { variant: 'error' });
      }
    },
  });

  return (
    <Dialog fullWidth {...props}>
      <DialogTitle fontWeight="bold">Редактировать статус клиента</DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item lg={6} md={6} xs={12}>
              <TextField
                name="name"
                label="Наименование"
                placeholder="Введите значение"
                value={values.name}
                onChange={handleChange}
                disabled={updateCustomerStatusResponse.isLoading}
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name ? errors.name : ''}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            type="button"
            color="secondary"
            variant="outlined"
            disabled={updateCustomerStatusResponse.isLoading}
            onClick={onClose}
          >
            Закрыть
          </Button>

          <Button type="submit" variant="contained" disabled={updateCustomerStatusResponse.isLoading}>
            Редактировать
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UpdateCustomerStatus;
